/**
 * How many days a leave request actually costs.
 *
 * One place owns this because the app previously disagreed with itself: the Apply form
 * displayed "0.5 day" for a half-day while the balance deducted a whole one, and a
 * Friday-to-Monday request cost four days because the count was calendar days with no
 * regard for weekends or public holidays — even though the same page renders those
 * holidays on its calendar.
 *
 * The rules, confirmed with the business:
 *   - a half-day session costs 0.5, whatever the dates say
 *   - otherwise count only working days: Saturdays, Sundays and public holidays are free
 */

import { HOLIDAYS_2026, Holiday } from '../data/holidays';

/** Every year we hold holiday data for. Anything outside this only excludes weekends. */
const HOLIDAY_SETS: Record<number, Holiday[]> = {
  2026: HOLIDAYS_2026,
};

const holidayDatesByYear: Record<number, Set<string>> = Object.fromEntries(
  Object.entries(HOLIDAY_SETS).map(([year, list]) => [year, new Set(list.map(h => h.date))])
);

const yearsWarnedAbout = new Set<number>();

/** Parse an ISO date as local midnight — `new Date('2026-08-03')` is UTC and can shift a day. */
const parseISO = (iso: string): Date | null => {
  const [year, month, day] = (iso || '').split('-').map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const isWeekend = (iso: string): boolean => {
  const date = parseISO(iso);
  if (!date) return false;
  const dow = date.getDay();
  return dow === 0 || dow === 6;
};

/**
 * Whether a date is a public holiday.
 *
 * Years with no holiday data return false, and say so once per year in the console rather
 * than silently treating every holiday that year as an ordinary working day.
 */
export const isHoliday = (iso: string): boolean => {
  const date = parseISO(iso);
  if (!date) return false;

  const year = date.getFullYear();
  const known = holidayDatesByYear[year];
  if (!known) {
    if (!yearsWarnedAbout.has(year)) {
      yearsWarnedAbout.add(year);
      console.warn(
        `[leaveDuration] no public-holiday data for ${year}; only weekends will be excluded ` +
        `from leave in that year. Add HOLIDAYS_${year} to data/holidays.ts to fix.`
      );
    }
    return false;
  }
  return known.has(iso);
};

/** A day the employee would otherwise have worked. */
export const isWorkingDay = (iso: string): boolean => !isWeekend(iso) && !isHoliday(iso);

/** The public holiday's name, when the date is one. */
export const holidayName = (iso: string): string | undefined => {
  const date = parseISO(iso);
  if (!date) return undefined;
  return HOLIDAY_SETS[date.getFullYear()]?.find(h => h.date === iso)?.name;
};

/**
 * Working days in an inclusive range.
 *
 * Returns 0 for an inverted or unparseable range rather than guessing — callers surface
 * that as "pick valid dates" instead of quietly booking a day.
 */
export const workingDaysBetween = (startISO: string, endISO: string): number => {
  const start = parseISO(startISO);
  const end = parseISO(endISO);
  if (!start || !end || end < start) return 0;

  let count = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    if (isWorkingDay(toISO(cursor))) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

/** Every date in an inclusive range, working day or not. */
export const datesBetween = (startISO: string, endISO: string): string[] => {
  const start = parseISO(startISO);
  const end = parseISO(endISO);
  if (!start || !end || end < start) return [];

  const dates: string[] = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    dates.push(toISO(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};

/** Shape shared by stored leave records and the in-progress Apply form. */
export interface LeaveDurationInput {
  startDate: string;
  endDate: string;
  session?: string | null;
}

/**
 * The number of days to charge against a balance — the single source of truth.
 *
 * A half-day session is always 0.5, even if it lands on a weekend or holiday: the employee
 * chose to take it, and refusing to charge for it would let half-days be booked for free.
 * A full-day request is charged only for the working days it covers.
 */
export const leaveDaysFor = (leave: LeaveDurationInput): number => {
  if (leave.session && leave.session !== 'FULL') return 0.5;
  return workingDaysBetween(leave.startDate, leave.endDate);
};

/** "3 days", "0.5 day", "1 day" — consistent wording wherever a duration is shown. */
export const formatLeaveDays = (days: number): string =>
  `${days} ${days === 1 ? 'day' : 'days'}`;
